/**
 * storageGuard — Transparent localStorage fallback to IndexedDB.
 *
 * Problem:
 *   iOS Safari has a 5MB hard quota on localStorage. Writing big unified_story_data
 *   throws QuotaExceededError and silently wipes the page's storage. This causes
 *   repeated recurring iOS crashes (P0 issue reported 5+ times).
 *
 * Solution:
 *   - Monkey-patch Storage.prototype.setItem/getItem
 *   - On quota error OR for LARGE_KEYS: route value to IndexedDB + in-memory cache
 *   - getItem transparently reads from mem cache if not in real localStorage
 *   - Pre-warm mem cache from IndexedDB at app startup
 *
 * Important:
 *   - Keeps the SYNC API of localStorage intact (no changes to 93 call sites)
 *   - Pre-warm is async; until it finishes, large keys return null — caller must
 *     handle null gracefully (most already do with fallback to backend fetch)
 *   - Nuclear wipe (FRESH_START_VERSION bump) must also clear this IDB store
 */
import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys, createStore } from 'idb-keyval';

const LARGE_KEYS = new Set([
  'unified_story_data',
  'ai_story_data',
  'past_story_manifest',
  'future_story_manifest',
  'cached_audio_data',
]);

// In-memory cache mirrors IndexedDB for SYNC getItem/setItem compatibility
const memCache = new Map();

// Dedicated IDB store
const store = createStore('taraanaya-storage-v1', 'large-kv');

// Pre-warm mem cache from IDB — runs once at import
let warmPromise = null;
async function warmCache() {
  if (warmPromise) return warmPromise;
  warmPromise = (async () => {
    try {
      if (typeof indexedDB === 'undefined') return;
      const allKeys = await idbKeys(store);
      await Promise.all(
        allKeys.map(async (k) => {
          try {
            const v = await idbGet(k, store);
            if (v !== undefined) memCache.set(k, v);
          } catch (_) {}
        })
      );
      if (allKeys.length) {
        console.log(`🔥 [storageGuard] Pre-warmed ${allKeys.length} large keys from IndexedDB`);
      }
    } catch (e) {
      console.warn('[storageGuard] warmCache error:', e);
    }
  })();
  return warmPromise;
}

// Install the shim
let installed = false;
function install() {
  if (installed || typeof window === 'undefined' || !window.Storage) return;

  const origSetItem = Storage.prototype.setItem;
  const origGetItem = Storage.prototype.getItem;
  const origRemoveItem = Storage.prototype.removeItem;
  const origClear = Storage.prototype.clear;

  // Patched setItem: try real → on quota error, fallback to IDB + mem
  Storage.prototype.setItem = function (key, value) {
    // LARGE_KEYS always go to IDB (avoids the 5MB risk entirely)
    if (this === window.localStorage && LARGE_KEYS.has(key)) {
      const strVal = typeof value === 'string' ? value : String(value);
      memCache.set(key, strVal);
      idbSet(key, strVal, store).catch((e) => {
        console.error(`[storageGuard] IDB set failed for ${key}:`, e);
      });
      // Also try original storage in case it fits (best effort, non-blocking)
      try {
        origSetItem.call(this, key, strVal);
      } catch (_) {
        // Silent — IDB is our primary
      }
      return;
    }

    try {
      origSetItem.call(this, key, value);
    } catch (e) {
      if (
        this === window.localStorage &&
        (e.name === 'QuotaExceededError' ||
          e.code === 22 ||
          e.code === 1014 || // Firefox
          /quota/i.test(e.message || ''))
      ) {
        console.warn(`⚠️ [storageGuard] Quota exceeded on "${key}" — routing to IndexedDB`);
        const strVal = typeof value === 'string' ? value : String(value);
        memCache.set(key, strVal);
        idbSet(key, strVal, store).catch((err) => {
          console.error(`[storageGuard] IDB fallback failed for ${key}:`, err);
        });
        return;
      }
      throw e;
    }
  };

  // Patched getItem: real → mem cache fallback
  Storage.prototype.getItem = function (key) {
    const fromReal = origGetItem.call(this, key);
    if (fromReal !== null) return fromReal;
    if (this === window.localStorage && memCache.has(key)) {
      return memCache.get(key);
    }
    return null;
  };

  // Patched removeItem: clear all layers
  Storage.prototype.removeItem = function (key) {
    origRemoveItem.call(this, key);
    if (this === window.localStorage) {
      memCache.delete(key);
      if (LARGE_KEYS.has(key) || memCache.size > 0) {
        idbDel(key, store).catch(() => {});
      }
    }
  };

  // Patched clear: wipe all layers (nuclear reset compatibility)
  Storage.prototype.clear = function () {
    origClear.call(this);
    if (this === window.localStorage) {
      memCache.clear();
      (async () => {
        try {
          if (typeof indexedDB === 'undefined') return;
          const allKeys = await idbKeys(store);
          await Promise.all(allKeys.map((k) => idbDel(k, store)));
        } catch (_) {}
      })();
    }
  };

  installed = true;
  console.log('🛡️  [storageGuard] localStorage shim installed (iOS 5MB quota safe)');
}

// Install immediately at import
install();

// Kick off pre-warm (non-blocking)
warmCache();

export { warmCache, LARGE_KEYS };
export default { warmCache, LARGE_KEYS };
